import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import Button from '../../../atoms/Button/Button';
import Card from '../../../atoms/Card/Card';
import Menu from '../../../atoms/Menu/Menu';
import RadioButton from '../../../atoms/RadioButton/RadioButton';
import { getFromLocalStorage, saveToLocalStorage } from '../../../Functions/LocalStorageUtils';
import { setHideAddDevicePopup } from '../../../redux/reducers/settingsReducer';
import { useAppDispatch, useAppSelector } from '../../../redux/store';

import styles from './GeneralSetting.module.scss';

type TieLinesMode = 'onHover' | 'always' | 'never';

const GeneralSetting: React.FC = () => {
    const dispatch = useAppDispatch();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const hideAddDevicePopup = useAppSelector((state) => state.settings.hide_add_device_popup);
    const [tutorialCompleted, setTutorialCompleted] = useState(getFromLocalStorage('tutorialCompleted') === 'true');
    const [actuatorsEasyMode, setActuatorsEasyMode] = useState(getFromLocalStorage('actuatorsEasyMode') === 'true');
    const [showBackgroundColors, setShowBackgroundColors] = useState(getFromLocalStorage('showBackgroundColors') === 'true');
    const [tieLinesMode, setTieLinesMode] = useState<TieLinesMode>((getFromLocalStorage('tieLinesMode') as TieLinesMode) || 'onHover');

    useEffect(() => {
        const stored = {
            hideAddDevicePopup: getFromLocalStorage('hideAddDevicePopup') === 'true',
            tutorialCompleted: getFromLocalStorage('tutorialCompleted') === 'true',
            actuatorsEasyMode: getFromLocalStorage('actuatorsEasyMode') === 'true',
            showBackgroundColors: getFromLocalStorage('showBackgroundColors') === '' ? true : getFromLocalStorage('showBackgroundColors') === 'true',
            tieLinesMode: (getFromLocalStorage('tieLinesMode') as TieLinesMode) || 'onHover'
        }
        dispatch(setHideAddDevicePopup(stored.hideAddDevicePopup));
        setTutorialCompleted(stored.tutorialCompleted);
        setActuatorsEasyMode(stored.actuatorsEasyMode);
        setShowBackgroundColors(stored.showBackgroundColors);
        setTieLinesMode(stored.tieLinesMode);
    }, [dispatch]);

    const settings: {
        setting: string,
        label: string, 
        active?: boolean, 
        type: 'checkbox' | 'multichoice', 
        multichoice?: { value: TieLinesMode, label: string }[]
        multichoiceSelected?: string
    }[] = [
        {
            setting: 'hideAddDevicePopup',
            label: t('settings.general_settings.hide_add_device_popup'),
            active: hideAddDevicePopup,
            type: 'checkbox'
        },
        {
            setting: 'tutorialCompleted',
            label: t('settings.general_settings.toggle_tutorial'),
            active: tutorialCompleted,
            type: 'checkbox'
        },
        {
            setting: 'actuatorsEasyMode',
            label: t('settings.general_settings.actuators_easy_mode'),
            active: actuatorsEasyMode,
            type: 'checkbox'
        },
        {
            setting: 'showBackgroundColors',
            label: t('settings.general_settings.show_background_colors'),
            active: showBackgroundColors,
            type: 'checkbox'
        },
        {
            setting: 'tieLinesMode',
            label: t('settings.general_settings.tie_lines_mode'),
            type: 'multichoice',
            multichoice: [
                { value: 'onHover', label: t('settings.general_settings.tie_lines_mode_options.on_hover') },
                { value: 'always', label: t('settings.general_settings.tie_lines_mode_options.always') },
                { value: 'never', label: t('settings.general_settings.tie_lines_mode_options.never') }
            ],
            multichoiceSelected: tieLinesMode
        }
    ];

    const handleCheckboxSettingChange = (s: {setting: string, label: string, active?: boolean}) => {
        if(s.active === undefined) return;
        saveToLocalStorage(s.setting, (!s.active).toString());
        switch(s.setting){
            case 'hideAddDevicePopup':
                dispatch(setHideAddDevicePopup(!s.active));
            break;
            case 'tutorialCompleted':
                setTutorialCompleted(!s.active);
                saveToLocalStorage('tutorialCompleted', (!s.active).toString());
            break;
            case 'actuatorsEasyMode':
                setActuatorsEasyMode(!s.active);
                saveToLocalStorage('actuatorsEasyMode', (!s.active).toString());
            break;
            case 'showBackgroundColors':
                setShowBackgroundColors(!s.active);
                saveToLocalStorage('showBackgroundColors', (!s.active).toString());
            break;
        }
    }

    const handleMultichoiceSettingChange = (setting: string, value: string) => {
        if (setting !== 'tieLinesMode') return;
        const typedValue = value as TieLinesMode;
        setTieLinesMode(typedValue);
        saveToLocalStorage('tieLinesMode', typedValue);
    }

    return (
        <div className={styles.Container}>
            <div>
                <h2 className={styles.Heading}>{t('settings.general_settings.heading')}</h2>
            </div>
            <Card width="36rem" padding="1.5rem">
                <Menu orientation="vertical">
                    {settings.map((s) => {
                        if(s.type === 'multichoice'){
                            return (
                                <Menu.Item key={s.setting} variant="primary">
                                    <Menu.ItemLeft>{s.label}</Menu.ItemLeft>
                                    <Menu.ItemRight>
                                        <Menu.MultiChoice
                                            options={s.multichoice || []}
                                            value={s.multichoiceSelected || ''}
                                            onChange={(value) => handleMultichoiceSettingChange(s.setting, value)}
                                        />
                                    </Menu.ItemRight>
                                </Menu.Item>
                            );
                        }else{
                            return (
                                <Menu.Item
                                    key={s.setting}
                                    variant="primary"
                                    isactive={s.active}
                                    onClick={() => handleCheckboxSettingChange(s)}
                                >
                                    <Menu.ItemLeft>{s.label}</Menu.ItemLeft>
                                    <Menu.ItemRight>
                                        <RadioButton group={`settings_general_${s.setting}`} checked={s.active} />
                                    </Menu.ItemRight>
                                </Menu.Item>
                            );
                        }
                    })}
                </Menu>
                <Button variant="fill" margin="1.5rem 0 0 0" padding=".5rem 1rem" 
                onClick={() => navigate('../../')} >
                    {t('save')}
                </Button>
            </Card>
        </div>
    );
}

export default GeneralSetting;